import React, { useEffect } from "react";
import Filters from ".";
import FastAPIClient from "../../client";
import config from "../../config";
const client = new FastAPIClient(config);
import { initial_subCategories } from "../../utils/Clients/mapping";
import { useNetwork } from "../NetworkProvider";

interface ClientFiltersProps {
    showLeads: boolean;
    subCategories: Record<string, string[]>;
    setSubCategories: React.Dispatch<
        React.SetStateAction<Record<string, string[]>>
    >;
    selectedFilters: Record<string, string[]>;
    setSelectedFilters: React.Dispatch<
        React.SetStateAction<Record<string, string[]>>
    >;
    expandedLevels: Record<string, boolean>;
    setExpandedLevels: React.Dispatch<
        React.SetStateAction<Record<string, boolean>>
    >;
    reset: () => void;
    user_id: string;
    statusFilters: Record<string, boolean[]>;
    setStatusFilters: React.Dispatch<
        React.SetStateAction<Record<string, boolean[]>>
    >;
    daysFilters: number[];
    setDaysFilters: React.Dispatch<React.SetStateAction<number[]>>;
    mobileFilterOpen: boolean;
    mobileFilters: Record<string, string[]>;
    setMobileFilters: React.Dispatch<
        React.SetStateAction<Record<string, string[]>>
    >;
    setIsFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
    mobileStatusFilters: Record<string, boolean[]>;
    setMobileStatusFilters: React.Dispatch<
        React.SetStateAction<Record<string, boolean[]>>
    >;
    mobileArrayFilters: number[];
    setMobileArrayFilters: React.Dispatch<React.SetStateAction<number[]>>;
}

const ClientFilters: React.FC<ClientFiltersProps> = ({
    showLeads,
    selectedFilters,
    setSelectedFilters,
    expandedLevels,
    setExpandedLevels,
    subCategories,
    setSubCategories,
    reset,
    user_id,
    statusFilters,
    setStatusFilters,
    daysFilters,
    setDaysFilters,
    mobileFilterOpen,
    mobileFilters,
    setMobileFilters,
    setIsFilterOpen,
    mobileStatusFilters,
    setMobileStatusFilters,
    mobileArrayFilters,
    setMobileArrayFilters,
}) => {
    const { isOnline } = useNetwork();
    const getClientSubCategories = async (subcategories) => {
        client
            .getClientSubCategories({ user_id, showLeads }, { subcategories })
            .then((data) => {
                setSubCategories({
                    ...Object.fromEntries(
                        Object.entries(initial_subCategories).map(
                            ([key, value]) => [
                                key,
                                Array.isArray(value)
                                    ? value
                                          .map(String)
                                          .sort((a, b) =>
                                              a
                                                  .toLowerCase()
                                                  .localeCompare(
                                                      b.toLowerCase()
                                                  )
                                          )
                                    : [],
                            ]
                        )
                    ),
                    ...Object.fromEntries(
                        Object.entries(data).map(([key, value]) => [
                            key,
                            Array.isArray(value)
                                ? value
                                      .map(String)
                                      .sort((a, b) =>
                                          a
                                              .toLowerCase()
                                              .localeCompare(b.toLowerCase())
                                      )
                                : [],
                        ])
                    ),
                } as Record<string, string[]>);
            })
            .catch((error) => {
                console.error("Error fetching subcategories:", error);
            });
    };

    const getClientSubCategoriesOffline = async () => {
        if (!user_id) return;

        try {
            const clients = await client.getUserClients(user_id, null);
            const filteredClients = clients.filter(
                (client) => client.is_lead === showLeads
            );

            // Create subcategories object from clients data
            const subCategoriesData: Record<string, string[]> = {};

            // Get unique values for each category from the clients list
            Object.keys(initial_subCategories).forEach((key) => {
                if (key === "visit_days") {
                    // Handle visit_days separately since it's a number array
                    const values: string[] = filteredClients
                        .filter((client) => Array.isArray(client[key]))
                        .flatMap((client) => {
                            const days = client[key] as number[];
                            return days.map(String);
                        });
                    subCategoriesData[key] = [...new Set(values)];
                } else {
                    // Handle other categories
                    const values: string[] = filteredClients
                        .filter(
                            (client) =>
                                client[key] != null && client[key] !== ""
                        )
                        .sort((a, b) => {
                            const labelA = String(a).toLowerCase();
                            const labelB = String(b).toLowerCase();
                            return labelA.localeCompare(labelB);
                        })
                        .map((client) => String(client[key]));

                    subCategoriesData[key] = [...new Set(values)];
                }
            });

            setSubCategories({
                ...Object.fromEntries(
                    Object.entries(initial_subCategories).map(
                        ([key, value]) => [
                            key,
                            Array.isArray(value) ? value.map(String) : value,
                        ]
                    )
                ),
                ...subCategoriesData,
            });
        } catch (error) {
            console.error("Error getting client subcategories offline:", error);
        }
    };

    useEffect(() => {
        if (isOnline) {
            const newObj = {};
            Object.entries(initial_subCategories)
                .filter(([key]) => key !== "visit_days")
                .forEach(([key]) => (newObj[key] = []));
            getClientSubCategories(newObj);
        } else {
            getClientSubCategoriesOffline();
        }
    }, [user_id, showLeads, isOnline]);
    return (
        <div className="h-full w-full">
            <Filters
                {...{
                    subCategories,
                    expandedLevels,
                    setExpandedLevels,
                    selectedFilters,
                    setSelectedFilters,
                    reset,
                    statusFilters,
                    setStatusFilters,
                    mobileFilterOpen,
                    mobileFilters,
                    setMobileFilters,
                    setIsFilterOpen,
                    mobileStatusFilters,
                    setMobileStatusFilters,
                    mobileArrayFilters,
                    setMobileArrayFilters,
                }}
                arrayFilters={daysFilters}
                setArrayFilters={setDaysFilters}
            />
        </div>
    );
};

export default ClientFilters;
